import { useState, useCallback } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { FormattedMessage } from 'react-intl';
import { Box, Button, FormControlLabel, Pagination, Switch } from 'components/ui';
import { usePaginate } from 'contexts/paginate';
import { useError } from 'contexts/error';
import { useInfo } from 'contexts/info';
import { TitleTypography, SubtitleTypography } from 'components/styles_components/typography';
import { DividerContainer } from 'components/styles_components/container';
import NotificationsQuery from 'queries/notification/index';
import UnreadNotificationsCountQuery from 'queries/notification/unread_count';
import MarkAllAsReadMutation from 'mutations/notification/mark_all_as_read';
import Notifications from 'components/notification/index';

const IndexContainer = () => {
  const { page, onPageChange } = usePaginate();
  const setError = useError();
  const setInfo = useInfo();
  const [unreadOnly, setUnreadOnly] = useState(false);

  const {
    data = {
      notifications: {
        notifications: [],
        notificationsTotal: 0,
        notificationsTotalPages: 0,
        unreadTotal: 0,
      },
    },
    loading,
  } = useQuery(NotificationsQuery, {
    variables: { page, unreadOnly: unreadOnly || null },
    cachePolicy: 'cache-and-network',
    onError: () => setError('error.notification.index'),
  });

  const [markAllAsRead, { loading: marking }] = useMutation(MarkAllAsReadMutation, {
    refetchQueries: [
      { query: UnreadNotificationsCountQuery },
      { query: NotificationsQuery, variables: { page, unreadOnly: unreadOnly || null } },
    ],
    onCompleted: () => setInfo('info.notification.all_marked_as_read'),
    onError: () => setError('error.notification.mark_all_as_read'),
  });

  const handleUnreadToggle = useCallback(
    (event) => {
      onPageChange(1);
      setUnreadOnly(event.target.checked);
    },
    [onPageChange]
  );

  const hasUnread = data.notifications.unreadTotal > 0;
  const totalPages = data.notifications.notificationsTotalPages;

  return (
    <>
      <TitleTypography variant="h1">
        <FormattedMessage id="navbar.notifications" />
      </TitleTypography>
      <SubtitleTypography variant="h2">
        <FormattedMessage
          id="notification.total"
          values={{ count: data.notifications.notificationsTotal ?? 0 }}
        />
      </SubtitleTypography>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={1}
        flexWrap="wrap"
        sx={{ mb: 2 }}
      >
        <FormControlLabel
          control={<Switch checked={unreadOnly} onChange={handleUnreadToggle} />}
          label={<FormattedMessage id="notification.unread_only" />}
        />
        <Button
          variant="contained"
          disabled={!hasUnread || marking}
          onClick={() => markAllAsRead({ variables: {} })}
        >
          <FormattedMessage id="notification.mark_all_as_read" />
        </Button>
      </Box>
      <Notifications loading={loading} notifications={data.notifications} unreadOnly={unreadOnly} />
      <Box display="flex" justifyContent="center">
        {totalPages > 1 && (
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_e, nextPage) => {
              onPageChange(nextPage);
            }}
          />
        )}
      </Box>
      <DividerContainer />
    </>
  );
};

export default IndexContainer;
